import React, { useMemo, useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ChefHat, Lock, CheckCircle, Search, Crown } from 'lucide-react';
import { FixedSizeGrid as Grid } from 'react-window';
import type { Material, CookbookRecipe, CustomRecipe } from '../types';
import { CONSTANTS } from '../data.js';
import { useCookbookResize } from '../hooks/useCookbookResize';
import { CookbookSidebarResizer } from '../ui/CookbookSidebarResizer';
import { useGameDataContext } from '../app/GameProviders';

interface Props {
  show: boolean;
  onClose: () => void;
  inventory: Material[];
  premiumUnlocked?: boolean;
}

interface GridItemProps {
  columnIndex: number;
  rowIndex: number;
  style: React.CSSProperties;
  data: {
    recipes: CookbookRecipe[];
    inventory: Material[];
    columnCount: number;
    premiumUnlocked: boolean;
  };
}

const GridItem: React.FC<GridItemProps> = React.memo(({ columnIndex, rowIndex, style, data }) => {
  const { recipes, inventory, columnCount, premiumUnlocked } = data;
  const index = rowIndex * columnCount + columnIndex;
  
  if (index >= recipes.length) {
    return <div style={style} />;
  }

  const recipe = recipes[index];
  const isPremiumRecipe = ['gourmet', 'exotic', 'magical'].includes(recipe.category);
  const isLocked = isPremiumRecipe && !premiumUnlocked && !recipe.isDiscovered;

  return (
    <div style={style} className="p-2">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`p-4 rounded-lg border transition-all h-full relative ${
          isLocked
            ? 'bg-slate-800/50 border-slate-600/50'
            : recipe.isDiscovered
            ? 'bg-gradient-to-br from-emerald-500/20 via-green-500/15 to-emerald-600/20 border-emerald-500/40'
            : recipe.hasIngredients
            ? 'bg-gradient-to-br from-amber-500/20 via-yellow-500/15 to-orange-500/20 border-amber-500/40'
            : 'bg-gradient-to-br from-slate-700/40 via-slate-600/30 to-slate-700/40 border-slate-600/40'
        }`}
      >
        {/* Premium Badge */}
        {isPremiumRecipe && (
          <div className="absolute top-2 right-2">
            <Crown className="w-4 h-4 text-purple-400" />
          </div>
        )}

        {/* Lock Overlay */}
        {isLocked && (
          <div className="absolute inset-0 bg-slate-900/80 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <Lock className="w-8 h-8 text-slate-500 mx-auto mb-2" />
              <p className="text-xs text-slate-400">Premium Cookbook Required</p>
            </div>
          </div>
        )}

        {/* Recipe Header */}
        <div className="flex items-center gap-3 mb-3">
          <div className="text-2xl">{recipe.icon}</div>
          <div className="flex-1">
            <h3 className="font-semibold text-white text-sm">{recipe.name}</h3>
            <p className="text-xs text-slate-400">{recipe.description}</p>
          </div>
          <div className="flex items-center">
            {isLocked ? (
              <Lock className="w-4 h-4 text-slate-500" />
            ) : recipe.isDiscovered ? (
              <CheckCircle className="w-4 h-4 text-emerald-400" />
            ) : recipe.hasIngredients ? (
              <div className="w-4 h-4 rounded-full bg-amber-400/20 border border-amber-400/50" />
            ) : (
              <Lock className="w-4 h-4 text-slate-500" />
            )}
          </div>
        </div>

        {!isLocked && (
          <>
            {/* Primary Ingredients */}
            <div className="space-y-2">
              <div className="text-xs font-medium text-slate-300 uppercase tracking-wide">
                Recipe ({recipe.type === 'triple' ? '3' : '2'} ingredients)
              </div>
              <div className="flex flex-wrap gap-1">
                {recipe.ingredients.map((ingredient, index) => {
                  const hasIngredient = inventory.some(item => 
                    item.id === ingredient.id && item.discovered
                  );
                  
                  return (
                    <div
                      key={`${recipe.id}-primary-${ingredient.id}-${index}`}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                        hasIngredient
                          ? 'bg-gradient-to-r from-emerald-500/30 to-green-500/30 text-emerald-200 border border-emerald-500/40'
                          : 'bg-gradient-to-r from-slate-600/40 to-slate-700/40 text-slate-400 border border-slate-600/40'
                      }`}
                    >
                      <span className="text-xs">{ingredient.icon}</span>
                      <span className="text-xs">{ingredient.name}</span>
                    </div>
                  );
                })}
              </div>
              
              {recipe.type === 'triple' && (
                <div className="text-xs text-amber-400 italic">
                  ⚡ Advanced Recipe (3 ingredients)
                </div>
              )}

              {/* Alternative Combinations */}
              {recipe.alternativeCombinations && recipe.alternativeCombinations.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-600/30">
                  <div className="text-xs font-medium text-slate-300 uppercase tracking-wide mb-1">
                    Alternative Methods
                  </div>
                  {recipe.alternativeCombinations.map((alt, altIndex) => (
                    <div key={`${recipe.id}-alt-${altIndex}`} className="mb-1">
                      <div className="flex flex-wrap gap-1">
                        {alt.ingredients.map((ingredient, ingIndex) => {
                          const hasIngredient = inventory.some(item => 
                            item.id === ingredient.id && item.discovered
                          );
                          
                          return (
                            <div
                              key={`${recipe.id}-alt-${altIndex}-${ingredient.id}-${ingIndex}`}
                              className={`flex items-center gap-1 px-1 py-0.5 rounded text-xs ${
                                hasIngredient
                                  ? 'bg-gradient-to-r from-purple-500/30 to-pink-500/30 text-purple-200 border border-purple-500/40'
                                  : 'bg-gradient-to-r from-slate-600/30 to-slate-700/30 text-slate-500 border border-slate-600/30'
                              }`}
                            >
                              <span className="text-xs">{ingredient.icon}</span>
                              <span className="text-xs">{ingredient.name}</span>
                            </div>
                          );
                        })}
                        {alt.type === 'triple' && (
                          <span className="text-xs text-amber-400 ml-1">⚡</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
});

export function Cookbook({ show, onClose, inventory, premiumUnlocked = false }: Props) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { sidebarWidth, onPointerDown } = useCookbookResize();
  const gameData = useGameDataContext();

  const cookbookData = useMemo(() => {
    const discoveredIds = new Set(inventory.filter(item => item.discovered).map(item => item.id));
    const recipeMap = new Map<string, CookbookRecipe>();

    const processRecipe = (combo: string, resultId: string, type: 'pair' | 'triple') => {
      if (!discoveredIds.has(resultId)) return;

      const resultMaterial = gameData.MATERIALS[resultId];
      const metadata = gameData.RECIPE_METADATA[resultId];
      const ingredientIds = combo.split('+');

      if (!resultMaterial || !metadata) return;

      const hasIngredients = ingredientIds.every(id => discoveredIds.has(id));
      const ingredients = ingredientIds.map(id => ({
        id,
        name: gameData.MATERIALS[id].name,
        icon: gameData.MATERIALS[id].icon,
      }));

      if (recipeMap.has(resultId)) {
        const existingRecipe = recipeMap.get(resultId)!;
        existingRecipe.alternativeCombinations = existingRecipe.alternativeCombinations || [];
        existingRecipe.alternativeCombinations.push({ ingredients, type, hasIngredients });
        if (hasIngredients) existingRecipe.hasIngredients = true;
      } else {
        recipeMap.set(resultId, {
          id: resultId,
          name: resultMaterial.name,
          icon: resultMaterial.icon,
          ingredients,
          category: metadata.category,
          description: metadata.description,
          isDiscovered: true,
          hasIngredients,
          type,
        });
      }
    };

    for (const [combo, resultId] of Object.entries(gameData.RECIPES)) {
      processRecipe(combo, resultId, 'pair');
    }
    for (const [combo, resultId] of Object.entries(gameData.TRIPLE_RECIPES)) {
      processRecipe(combo, resultId, 'triple');
    }

    return Array.from(recipeMap.values());
  }, [inventory, gameData]);

  const categories = useMemo(() => {
    const cats = Object.entries(gameData.COOKBOOK_CATEGORIES)
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => a.order - b.order);
    
    return [{ key: 'all', name: 'All Recipes', order: 0, color: '#64748b' }, ...cats];
  }, [gameData.COOKBOOK_CATEGORIES]);

  const filteredRecipes = useMemo(() => {
    let filtered = cookbookData;
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(recipe => recipe.category === selectedCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(recipe => 
        recipe.name.toLowerCase().includes(query) ||
        recipe.description.toLowerCase().includes(query) ||
        recipe.ingredients.some(ing => ing.name.toLowerCase().includes(query)) ||
        (recipe.alternativeCombinations && recipe.alternativeCombinations.some(alt => 
          alt.ingredients.some(ing => ing.name.toLowerCase().includes(query))
        ))
      );
    }
    
    return filtered.sort((a, b) => {
      if (a.isDiscovered !== b.isDiscovered) {
        return a.isDiscovered ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  }, [cookbookData, selectedCategory, searchQuery]);

  const discoveredCount = useMemo(() => cookbookData.length, [cookbookData]);

  const totalAvailableCount = useMemo(() => {
    const allResults = new Set<string>();
    Object.values(gameData.RECIPES).forEach((id) => allResults.add(id));
    Object.values(gameData.TRIPLE_RECIPES).forEach((id) => allResults.add(id));
    return allResults.size;
  }, [gameData]);

  const gridData = useMemo(() => ({
    recipes: filteredRecipes,
    inventory,
    columnCount: CONSTANTS.COOKBOOK_COLUMNS,
    premiumUnlocked
  }), [filteredRecipes, inventory, premiumUnlocked]);

  const rowCount = Math.ceil(filteredRecipes.length / CONSTANTS.COOKBOOK_COLUMNS);

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-gradient-to-br from-slate-900/95 via-amber-900/20 to-orange-900/30 z-[2000] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gradient-to-br from-slate-800/95 via-amber-900/10 to-orange-900/10 backdrop-blur-md rounded-xl shadow-2xl w-full max-w-6xl h-[80vh] flex flex-col border border-amber-500/30"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-amber-500/20 bg-gradient-to-r from-amber-600/10 to-orange-600/10 rounded-t-xl">
            <div className="flex items-center gap-3">
              <ChefHat className="w-6 h-6 text-amber-400" />
              <h2 className="text-2xl font-bold text-amber-100">Recipe Cookbook</h2>
              {premiumUnlocked && (
                <div className="flex items-center gap-1 bg-gradient-to-r from-purple-600/30 to-pink-600/30 text-purple-300 px-2 py-1 rounded-full text-sm border border-purple-500/30">
                  <Crown className="w-3 h-3" />
                  Premium
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-amber-200">
                Discovered: <span className="text-emerald-400 font-semibold">{discoveredCount}</span>
                <span className="text-slate-500">/</span>
                <span className="text-amber-100">{totalAvailableCount}</span>
              </div>
              <button
                onClick={onClose}
                className="text-amber-300 hover:text-white p-1"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar */}
            <div 
              className="border-r border-amber-500/20 p-4 flex flex-col gap-4 bg-gradient-to-b from-slate-800/60 via-amber-900/10 to-orange-900/10 relative"
              style={{ width: `${sidebarWidth}px` }}
            >
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400" />
                <input
                  type="text"
                  placeholder="Search recipes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-slate-700/50 border border-amber-500/30 rounded-md pl-9 pr-3 py-2 text-sm text-white placeholder-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Categories */}
              <div className="flex flex-col gap-1 overflow-y-auto max-h-96">
                {categories.map(category => {
                  const isPremiumCategory = ['gourmet', 'exotic', 'magical'].includes(category.key);
                  
                  return (
                    <button
                      key={category.key}
                      onClick={() => setSelectedCategory(category.key)}
                      className={`flex items-center justify-between p-2 rounded-md text-left transition-colors ${
                        selectedCategory === category.key
                          ? 'bg-gradient-to-r from-amber-600/30 to-orange-600/30 text-amber-200 border border-amber-600/40'
                          : 'hover:bg-gradient-to-r hover:from-slate-700/50 hover:to-amber-900/20 text-amber-100'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{category.name}</span>
                        {isPremiumCategory && (
                          <Crown className="w-3 h-3 text-purple-400" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Resizer */}
              <CookbookSidebarResizer onPointerDown={onPointerDown} />
            </div>

            {/* Main Content */}
            <div className="flex-1 bg-gradient-to-br from-slate-900/60 via-amber-900/5 to-orange-900/5">
              {filteredRecipes.length > 0 ? (
                <Grid
                  columnCount={CONSTANTS.COOKBOOK_COLUMNS}
                  columnWidth={CONSTANTS.COOKBOOK_CARD_WIDTH}
                  height={window.innerHeight * 0.8 - 120}
                  rowCount={rowCount}
                  rowHeight={CONSTANTS.COOKBOOK_CARD_HEIGHT}
                  width={window.innerWidth - sidebarWidth - 64}
                  itemData={gridData}
                  overscanRowCount={CONSTANTS.COOKBOOK_OVERSCAN_COUNT}
                >
                  {GridItem}
                </Grid>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center py-12">
                    <ChefHat className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                    <p className="text-amber-200">No recipes found matching your criteria.</p>
                    <p className="text-xs text-amber-300 mt-2">Keep experimenting to discover new recipes!</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
